import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Download,
  X,
  BookOpen,
  LogIn,
  FolderKanban,
  LayoutDashboard,
  Wrench,
  FileEdit,
  Shield as ShieldIcon,
  CheckCircle,
  FileText,
  Truck,
  ClipboardCheck,
  User,
  ChevronRight,
  ChevronDown,
  Calculator,
  History as HistoryIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const sections = [
  { id: 'getting-started', label: '1. Getting Started', icon: LogIn },
  { id: 'project-estimator', label: '2. Project Estimator', icon: Calculator },
  { id: 'projects', label: '3. Projects', icon: FolderKanban },
  { id: 'working-project', label: '4. Working in a Project', icon: LayoutDashboard },
  { id: 'tools-screen', label: '5. The Tools Screen', icon: Wrench },
  { id: 'authoring', label: '6. Authoring Data Modules', icon: FileEdit },
  { id: 'brex-builder', label: '7. BREX Builder', icon: ShieldIcon },
  { id: 'brex-validation', label: '8. BREX Validation', icon: CheckCircle },
  { id: 'dml-builder', label: '9. DML Builder', icon: FileText },
  { id: 'pdf-publish', label: '10. Publishing to PDF', icon: FileText },
  { id: 'delivery', label: '11. Delivery', icon: Truck },
  { id: 'ste-check', label: '12. STE Check', icon: ClipboardCheck },
  { id: 'account-security', label: '13. Your Account', icon: User },
  { id: 'history-audit', label: '14. History & Audit', icon: HistoryIcon },
];

const Tip = ({ children }) => (
  <div className="border-l-4 border-amber-500 bg-amber-50 rounded-r-lg px-4 py-3 my-4">
    <p className="text-sm text-slate-700"><span className="font-semibold text-amber-700">Tip:</span> {children}</p>
  </div>
);

const SectionHeading = ({ id, children }) => (
  <h2 id={id} className="text-xl font-bold text-purple-800 mt-10 mb-3 scroll-mt-6">{children}</h2>
);

const SubHeading = ({ children }) => (
  <h3 className="text-base font-semibold text-slate-800 mt-6 mb-2">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-sm leading-relaxed text-slate-700 mb-2">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="list-disc list-outside ml-6 mb-3 space-y-1">
    {items.map((item, i) => (
      <li key={i} className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: item }} />
    ))}
  </ul>
);

const NumberedList = ({ items }) => (
  <ol className="list-decimal list-outside ml-6 mb-3 space-y-1">
    {items.map((item, i) => (
      <li key={i} className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: item }} />
    ))}
  </ol>
);

const Figure = ({ src, caption, token }) => (
  <figure className="my-5">
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
      <img
        src={`${API}/api/user-guide/images/${src}?token=${token}`}
        alt={caption}
        className="w-full h-auto"
        loading="lazy"
        onError={(e) => { e.target.style.display = 'none'; }}
        style={{ maxHeight: '400px', objectFit: 'contain' }}
      />
    </div>
    <figcaption className="text-xs text-slate-500 italic text-center mt-2">{caption}</figcaption>
  </figure>
);


// Map current route path to the relevant help section
const PATH_TO_SECTION = {
  '/dashboard/tools/delivery': 'delivery',
  '/dashboard/tools/dml-builder': 'dml-builder',
  '/dashboard/tools/brex-validation': 'brex-validation',
  '/dashboard/tools/brex-builder': 'brex-builder',
  '/dashboard/tools/author': 'authoring',
  '/dashboard/tools/pdf-publish': 'pdf-publish',
  '/dashboard/tools/ste-check': 'ste-check',
  '/dashboard/tools': 'tools-screen',
  '/dashboard/projects': 'projects',
  '/dashboard/settings': 'account-security',
  '/dashboard/security': 'account-security',
  '/dashboard/team': 'getting-started',
  '/dashboard/resources': 'getting-started',
  '/dashboard/history': 'history-audit',
  '/dashboard/estimator': 'project-estimator',
  '/dashboard': 'getting-started',
};

function sectionForPath(pathname) {
  // Exact match first
  if (PATH_TO_SECTION[pathname]) return PATH_TO_SECTION[pathname];
  // Project dashboard (/dashboard/projects/:id)
  if (/^\/dashboard\/projects\/[^/]+$/.test(pathname)) return 'working-project';
  // Fallback
  return 'getting-started';
}


export default function HelpPanel({ onClose, currentPath }) {
  const { token } = useAuth();
  const initialSection = sectionForPath(currentPath || '/dashboard');
  const [activeSection, setActiveSection] = useState(initialSection);
  const [downloading, setDownloading] = useState(false);
  const [expandedToc, setExpandedToc] = useState(true);
  const hasScrolled = useRef(false);

  // Auto-scroll to the context-sensitive section on mount
  useEffect(() => {
    if (!hasScrolled.current && initialSection !== 'getting-started') {
      const timer = setTimeout(() => {
        const el = document.getElementById(initialSection);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        hasScrolled.current = true;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [initialSection]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/user-guide/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'MySpecWorkspace-User-Guide.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('User Guide downloaded');
    } catch {
      toast.error('Failed to download User Guide');
    } finally {
      setDownloading(false);
    }
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    const go = () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    go();
    // Re-align after lazy-loaded figures shift the layout
    setTimeout(go, 700);
    setTimeout(go, 1600);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-stretch justify-end" data-testid="help-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="bg-white w-full max-w-4xl shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
        data-testid="help-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-purple-700" />
            <h1 className="text-lg font-bold text-slate-900">MySpecWorkspace User Guide</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="help-download-pdf-btn"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="help-close-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body: sidebar + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar TOC */}
          <div className="w-56 shrink-0 border-r border-slate-200 bg-slate-50/50 overflow-y-auto">
            <button
              className="flex items-center gap-2 px-4 py-3 w-full text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-100"
              onClick={() => setExpandedToc(!expandedToc)}
              data-testid="help-toc-toggle"
            >
              {expandedToc ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Contents
            </button>
            {expandedToc && (
              <nav className="pb-4">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`flex items-center gap-2 px-4 py-2 w-full text-left text-xs transition-colors ${
                      activeSection === s.id
                        ? 'bg-purple-50 text-purple-800 font-semibold border-r-2 border-purple-600'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`help-toc-${s.id}`}
                  >
                    <s.icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Main content */}
          <ScrollArea className="flex-1">
            <div className="px-8 py-6 max-w-none prose-sm" data-testid="help-content">

              {/* Cover banner */}
              <div className="flex flex-col items-center text-center mb-8 pb-6 border-b border-slate-200">
                <img
                  src={`${API}/api/user-guide/images/tim_consulting_logo.jpg?token=${token}`}
                  alt="TIM Consulting"
                  className="h-20 w-auto object-contain mb-4"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h1 className="text-2xl font-bold text-slate-900">MySpecWorkspace</h1>
                <p className="text-base text-slate-500 mt-1">User Guide</p>
                <p className="text-xs text-slate-400 mt-2">Your complete guide to creating, checking, and publishing S1000D technical data</p>
              </div>

              {/* ════════════════════════════════════════ */}
              {/* 1. Getting Started */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="getting-started">1. Getting Started</SectionHeading>

              <SubHeading>1.1 What is MySpecWorkspace?</SubHeading>
              <P>
                MySpecWorkspace is a web-based portal for creating, checking, and publishing S1000D technical data.
                It provides a complete suite of tools for managing your S1000D documentation projects, from initial
                authoring through to final delivery.
              </P>
              <P>With MySpecWorkspace, you can:</P>
              <BulletList items={[
                'Create and manage S1000D projects with full lifecycle support',
                'Author data modules with real-time XML preview',
                'Build and validate Business Rules Exchange (BREX) documents',
                'Create and manage Data Module Lists (DMLs)',
                'Publish professional PDFs with standard or custom stylesheets',
                'Check text against Simplified Technical English (STE) Issue 9 rules',
                'Compare delivery notes against data module lists',
              ]} />

              <SubHeading>1.2 Logging In</SubHeading>
              <P>
                To access MySpecWorkspace, open your web browser (Chrome or Edge recommended) and navigate to
                the application URL provided by your administrator. You will see the login screen.
              </P>
              <Figure src="01_login.jpeg" caption="Figure 1 — The MySpecWorkspace login screen" token={token} />
              <P>
                Enter your email address and password, then click <strong>Sign In</strong>. If this is your first time,
                you may need to create an account using the <strong>Create Account</strong> tab. Your company administrator
                will provide your initial credentials.
              </P>
              <Tip>
                Use a strong password with at least 8 characters. Your administrator may also enable
                Multi-Factor Authentication (MFA) for added security. If MFA is enabled, you will need an authenticator
                app such as Google Authenticator or Microsoft Authenticator to generate one-time codes.
              </Tip>

              <SubHeading>1.3 Finding Your Way Around</SubHeading>
              <P>
                After logging in, you will see the main dashboard. The top navigation bar provides quick access
                to all areas of MySpecWorkspace. The dashboard gives you an at-a-glance view of your workspace,
                including quick access cards for Projects, Tools, and Resources.
              </P>
              <Figure src="02_dashboard.jpeg" caption="Figure 2 — The main dashboard after logging in" token={token} />
              <BulletList items={[
                '<strong>Dashboard</strong> — Your home screen with quick links to Projects, Tools, and Resources. Shows Quick Access to Tools cards.',
                '<strong>Projects</strong> — View and manage all your S1000D projects. Each project has its own dashboard with KPIs.',
                '<strong>Team</strong> — Manage team members within your company (subject to your company\'s licence limit of 1–5 users).',
                '<strong>Tools</strong> — Access the full suite of S1000D tools: Delivery, DML Builder, BREX Validation, BREX Builder, Author, PDF Publish, and STE Check.',
                '<strong>Resources</strong> — Download available PDFs, spreadsheets, and view training videos provided by your administrator.',
                '<strong>Help (?)</strong> — Click the question mark icon next to your name to open this user guide at any time.',
              ]} />

              <SubHeading>1.4 Team Management</SubHeading>
              <P>
                The <strong>Team</strong> screen lets you view and manage the user accounts within your company.
                Every company has a licence limit (a maximum number of seats), and the seat gauge in the
                top-right corner shows how many are in use. The first user of a company is the
                <strong> Primary</strong> user (shown with a crown badge) — only the Primary user can add
                team members and control which licensed products and projects each member can access.
              </P>
              <Figure src="23_team.jpeg" caption="Figure 2a — The Team Management screen with Product Configuration and member list" token={token} />
              <P>The Team screen contains three areas:</P>
              <BulletList items={[
                '<strong>Seat Gauge</strong> — Shows seats used vs. your company\'s licence limit. The bar turns amber when you are one seat from the limit and red when the limit is reached.',
                '<strong>Product Configuration</strong> — Lists the products (tools) licensed to your company. If an administrator has assigned a named configuration, its name is displayed; otherwise all tools are available.',
                '<strong>Team Members</strong> — Lists every user in your company with their access summary, MFA status, and Active/Inactive state.',
              ]} />

              <P><strong>To add a team member (Primary user only):</strong></P>
              <NumberedList items={[
                'Click <strong>Add Team Member</strong> in the top-right corner. The button is disabled if all seats are used.',
                'Enter the member\'s <strong>First Name</strong>, <strong>Last Name</strong>, and <strong>Email</strong>. Shared addresses such as info@, sales@ or support@ are not allowed.',
                'Set a <strong>Temporary Password</strong> (minimum 6 characters). The member will be asked to change it on their first login.',
                'Click <strong>Create Team Member</strong>. The new account appears in the Team Members list immediately.',
              ]} />

              <P><strong>To control which products a member can use:</strong></P>
              <NumberedList items={[
                'Click <strong>Manage Access</strong> next to the member\'s name (available to the Primary user only — the Primary user always has access to all tools).',
                'Tick or untick the licensed products the member should be able to open. Use <strong>Select all</strong> or <strong>Clear</strong> to change everything at once.',
                'Click <strong>Save Access</strong>. The member\'s access summary badge updates (e.g., "8 of 9 tools"). Tools the member cannot access are hidden or blocked when they log in.',
              ]} />
              <P><strong>To control which projects a member can see (Primary user only):</strong></P>
              <Figure src="24_manage_projects.jpeg" caption="Figure 2b — The Manage Project Access dialog with the All projects option and per-project checkboxes" token={token} />
              <NumberedList items={[
                'Click <strong>Manage Projects</strong> next to the member\'s name. The dialog lists every project in your company.',
                'Leave <strong>All projects</strong> ticked to give the member unrestricted access — this includes any new projects created in future.',
                'To restrict access, untick <strong>All projects</strong> and tick only the projects the member should see. Use <strong>Select all</strong> or <strong>Clear</strong> to change everything at once.',
                'Click <strong>Save Access</strong>. The member\'s project badge updates (e.g., "1 of 2 projects").',
              ]} />
              <P>When a member has a restricted project list:</P>
              <BulletList items={[
                'They only see their granted projects on the Projects screen and in every tool\'s project selector.',
                'New projects are <strong>hidden</strong> from them until the Primary user grants access via this dialog.',
                'They cannot create or import projects — the Create and Import buttons are hidden for them.',
              ]} />
              <Tip>
                If your company has reached its licence limit, an amber warning banner appears and the
                Add Team Member button is disabled. Contact your administrator to increase the limit.
              </Tip>

              {/* ════════════════════════════════════════ */}
              {/* 2. Project Estimator */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="project-estimator">2. Project Estimator</SectionHeading>
              <P>
                The Project Estimator is a comprehensive cost-estimation and planning workspace for S1000D documentation
                projects. It helps you scope the work, estimate effort, calculate costs, manage risks, and generate
                bid-ready summaries — all from a single integrated tool.
              </P>
              <P>
                Access the Project Estimator by clicking the <strong>Project Estimator</strong> card on the main
                Dashboard, or from the Quick Access section.
              </P>
              <Figure src="14_estimator.jpeg" caption="Figure 3 — Project Estimator workspace showing the System Breakdown tab" token={token} />

              <SubHeading>2.1 Workspace Layout</SubHeading>
              <P>
                The estimator opens with the <strong>System Breakdown</strong> tab selected by default. The workspace
                is divided into three areas:
              </P>
              <BulletList items={[
                '<strong>Header Bar</strong> — Contains the estimate title, currency selector, tag management, and Save/Load controls. Also shows tab group navigation (SCOPE, PLAN, BID, LIBRARY).',
                '<strong>Main Content Area</strong> — Displays the active tab content. Each tab provides a specialised editor or viewer for a different aspect of the estimate.',
                '<strong>Sticky Footer</strong> — A persistent bottom bar showing running totals: Total Hours, Blended Rate, Module/Illustration counts, FTE, Base Cost, and Grand Total. These update in real time as you modify the estimate.',
              ]} />

              <SubHeading>2.2 Tab Groups</SubHeading>
              <P>The 15 tabs are organised into four logical groups:</P>
              <BulletList items={[
                '<strong>SCOPE</strong> — System Breakdown, Data Modules, Illustrations, DML, and BREX. These tabs define <em>what</em> needs to be produced.',
                '<strong>PLAN</strong> — Roles, Rate Cards, Resourcing, and Schedule. These tabs define <em>who</em> does the work, at what rates, and <em>when</em>.',
                '<strong>BID</strong> — Risk, Bid Price, Summary, and Readiness. These tabs calculate the final price with risk contingencies and generate a bid-ready overview.',
                '<strong>LIBRARY</strong> — Compare, Portfolio, and Projects. These tabs let you save, load, compare, and manage multiple estimates.',
              ]} />

              <SubHeading>2.3 System Breakdown (Default Tab)</SubHeading>
              <P>
                The System Breakdown Structure (SBS) tab is the starting point for any new estimate. It lets you
                define the hierarchical breakdown of systems, sub-systems, and assemblies that make up your project scope.
              </P>
              <P>Key features of the SBS tab:</P>
              <BulletList items={[
                '<strong>Tree Editor</strong> — Add, rename, and nest systems in a drag-friendly tree view. Each system can have DM and illustration counts assigned.',
                '<strong>Excel Import</strong> — Bulk-import your system breakdown from a spreadsheet using the SBS Importer. Choose between <em>Replace</em> (clear existing data) or <em>Merge</em> (combine with existing). Download a template or sample file to see the expected format.',
                '<strong>Roll-up Cards</strong> — Automatically calculated summaries showing total data modules, illustrations, and effort hours per system branch.',
                '<strong>DMC Inventory Preview</strong> — Preview the data module codes that will be generated based on your system breakdown and information codes.',
              ]} />

              <SubHeading>2.4 Key Workflow</SubHeading>
              <P>A typical estimation workflow follows these steps:</P>
              <NumberedList items={[
                '<strong>Define Scope</strong> — Use the System Breakdown tab to define your project structure. Add Data Modules and Illustrations counts per system.',
                '<strong>Configure DML and BREX</strong> — Set up the DML parameters and any BREX-related effort in the respective tabs.',
                '<strong>Set Up Roles and Rates</strong> — Define team roles (Author, Illustrator, Reviewer, etc.) with hourly or daily rate cards.',
                '<strong>Plan Resourcing</strong> — Allocate FTE and working days. The tool calculates effective capacity automatically.',
                '<strong>Assess Risk</strong> — Use the Risk tab to define risk factors and contingency percentages that feed into the final price.',
                '<strong>Build Bid Price</strong> — The Bid Price tab combines base cost, overheads, contingencies, and margin into a final bid figure.',
                '<strong>Review Summary</strong> — The Summary tab provides a complete overview with all calculations, ready for export or presentation.',
                '<strong>Check Readiness</strong> — The Readiness tab shows a checklist of required inputs and flags any missing data before finalising your estimate.',
              ]} />

              <SubHeading>2.5 Save and Load</SubHeading>
              <P>
                Estimates can be saved and loaded using the controls in the header bar. You can maintain multiple
                named estimates and switch between them. The Compare tab (in the LIBRARY group) lets you view
                saved estimates side-by-side, while the Portfolio tab tracks all estimates across your organisation.
              </P>
              <Tip>
                The sticky footer at the bottom of the screen always shows the current Grand Total — you can see
                the cost impact of any change immediately. The footer also indicates whether BREX effort has been
                merged into the total (shown as &ldquo;Grand Total · incl. BREX&rdquo;).
              </Tip>

              {/* ════════════════════════════════════════ */}
              {/* 3. Projects */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="projects">3. Projects</SectionHeading>
              <P>
                Projects are the foundation of your work in MySpecWorkspace. Each project represents a single
                S1000D documentation effort and contains all related data modules, business rules, and delivery records.
                Projects are isolated per company — only users within your company can see and access your projects.
              </P>
              <Figure src="03_projects.jpeg" caption="Figure 4 — The Projects screen showing all your projects" token={token} />

              <SubHeading>3.1 Creating a New Project</SubHeading>
              <P>
                Click the <strong>+ Create New Project</strong> button in the top-right corner of the Projects screen.
                A modal will appear prompting you to enter the following fields:
              </P>
              <BulletList items={[
                '<strong>Project Title</strong> — A descriptive name for your project (e.g., "AH-64D Maintenance Manual")',
                '<strong>Description</strong> — A brief summary of the project\'s purpose and scope',
                '<strong>Model Identification Code</strong> — The S1000D model ident code (e.g., "AH64D"). This is used in all generated data module filenames.',
                '<strong>S1000D Issue</strong> — The applicable S1000D issue version (e.g., Issue 5.0). Select from the dropdown.',
                '<strong>SNS Code</strong> — The System, Subsystem, and Assembly codes. Use the searchable dropdown to find your system code.',
              ]} />
              <Tip>Each project is isolated to your company. Other companies cannot see or access your projects. Your company can have multiple active projects running concurrently.</Tip>

              <SubHeading>3.2 Project Cards</SubHeading>
              <P>
                Each project is displayed as a card showing the project title, S1000D issue version, SNS code,
                description, and a health score. The health score (shown as a coloured circle) reflects overall
                project compliance across schema, BREX, STE, and delivery metrics. Click on any project card to
                open its dedicated Project Dashboard.
              </P>
              <BulletList items={[
                '<strong>Green circle</strong> — Project health is good; all metrics are within acceptable ranges',
                '<strong>Amber circle</strong> — One or more metrics are below target but above critical thresholds',
                '<strong>Red circle</strong> — One or more metrics require immediate attention',
              ]} />

              <SubHeading>3.3 Project Settings</SubHeading>
              <P>
                Within each project, the Settings area allows you to configure project-specific options. The most
                important setting is <strong>Technical Nouns</strong> — a list of project-specific technical terms
                that the STE Check tool will recognise as valid. You can add nouns individually, or import them from
                an Excel spreadsheet.
              </P>

              <SubHeading>3.4 Delivery Milestones</SubHeading>
              <P>
                Each project can define delivery dates in the project modal (Configuration tab). Every delivery
                date can carry a <strong>milestone name</strong> (e.g., "IPD Delivery", "Final Delivery") so that
                dashboards and forecasts refer to your milestones by name instead of a generic "Delivery 1".
              </P>
              <Figure src="22_milestone_labels.jpeg" caption="Figure 4a — Delivery dates with milestone names in the project modal" token={token} />
              <P>To add a named delivery milestone:</P>
              <NumberedList items={[
                'Open the project modal — from the Project Dashboard, go to <strong>Tools and Actions</strong> and click <strong>Edit Project</strong> (or use <strong>+ Create New Project</strong>)',
                'Select the <strong>Configuration</strong> tab and scroll to <strong>Delivery Dates</strong>',
                'Pick the date with the <strong>Add delivery date</strong> calendar button',
                'Type the milestone name in the <strong>Milestone name</strong> field (e.g., "IPD Delivery")',
                'Click the <strong>+</strong> button to add the milestone — it appears in the list below where the name remains editable at any time',
                'Tick <strong>Verification Certification Complete</strong> once the milestone has been formally verified, then <strong>Save</strong> the project',
              ]} />
              <Tip>
                The next unverified future milestone is used as the target for the Forecast card on the Project
                Dashboard (see Section 4.5) — its name appears in the "Required Pace" tile.
              </Tip>

              {/* ════════════════════════════════════════ */}
              {/* 4. Working in a Project */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="working-project">4. Working in a Project</SectionHeading>
              <P>
                Click on any project card to open the Project Dashboard. This is your central hub for monitoring
                and managing all aspects of your S1000D project. The dashboard provides real-time KPIs, conformance
                trends, and quick access to all tools.
              </P>
              <Figure src="04_project_dashboard.jpeg" caption="Figure 5 — The Project Dashboard showing KPIs and conformance data" token={token} />

              <SubHeading>4.1 Dashboard Tabs</SubHeading>
              <P>The project dashboard has five tabs, each providing a different view of your project data:</P>
              <BulletList items={[
                '<strong>Default</strong> — Overview with six KPI tiles (Schema Pass Rate, BREX Compliance, STE Compliance, Delivery Match, Validations Run, Comparisons). Also shows conformance trend charts, the STE Compliance summary card, the <strong>Forecast</strong> card, and the <strong>Publication Builds</strong> card.',
                '<strong>BREX</strong> — Business rules overview showing BREX-related metrics, rule compliance percentages, and validation history.',
                '<strong>DMRL</strong> — Data Module Requirements List tracking. Shows which required data modules exist and which are outstanding.',
                '<strong>Tools and Actions</strong> — Quick launch buttons for all S1000D tools, pre-configured for this project.',
                '<strong>History</strong> — Complete activity log of all actions performed on the project, including validations, exports, and deliveries.',
              ]} />

              <SubHeading>4.2 STE Compliance Summary</SubHeading>
              <P>
                The Default tab includes an STE Compliance card that shows the results of the most recent
                ASD-STE100 Issue 9 check. This card provides a quick overview without needing to open the STE Check tool.
              </P>
              <Figure src="04b_ste_summary.jpeg" caption="Figure 6 — STE Compliance summary on the Project Dashboard" token={token} />
              <BulletList items={[
                'Overall compliance percentage (colour-coded: green ≥90%, amber ≥70%, red <70%)',
                'Number of DMs checked, compliant, and non-compliant',
                'Total violations found across all data modules',
                'Rule-by-rule breakdown (STE-1 through STE-7) showing pass/fail counts',
                'List of non-compliant data modules with their specific failed rules',
              ]} />

              <SubHeading>4.3 Project Status (RAG)</SubHeading>
              <P>The project status banner at the top uses a traffic-light (RAG) system to give an instant health indicator:</P>
              <BulletList items={[
                '<strong>Green</strong> — All metrics meet targets (Schema ≥98%, BREX ≥95%, STE ≥90%)',
                '<strong>Amber</strong> — One or more metrics are below target but above critical thresholds',
                '<strong>Red</strong> — One or more metrics require immediate attention',
              ]} />

              <SubHeading>4.4 BREX Modules</SubHeading>
              <P>
                The BREX tab of the Project Dashboard includes a <strong>BREX Modules</strong> card that lets you
                configure and manage the BREX module references used across the project. These references are critical
                for DDN generation, compliance tracking, and ensuring your project references the correct
                business rules at every level of the organisation hierarchy.
              </P>
              <Figure src="15_brex_modules.jpeg" caption="Figure 7 — BREX Modules card on the Project Dashboard showing configured module slots" token={token} />
              <P>The card defines five BREX module slots, each representing a different organisational level:</P>
              <BulletList items={[
                '<strong>Project</strong> — The project-specific BREX module DMC. This field is <em>auto-populated</em> when you generate a BREX document using the BREX Builder tool.',
                '<strong>S1000D Default</strong> — The default S1000D BREX reference (e.g., the standard S1000D BREX for your issue version).',
                '<strong>Department</strong> — Department-level BREX module, if applicable.',
                '<strong>Division</strong> — Division-level BREX module.',
                '<strong>Organisation</strong> — Top-level organisation BREX module.',
              ]} />
              <P>To configure BREX Modules:</P>
              <NumberedList items={[
                'Navigate to the Project Dashboard and select the <strong>BREX</strong> tab',
                'Click the <strong>BREX Modules</strong> card to expand it',
                'Click <strong>Edit BREX Modules</strong> to enter edit mode',
                'For each module slot, either type the DMC reference directly in the text field, or click the <strong>Upload</strong> button (arrow icon) to upload an XML file — the tool will automatically extract the DMC from the uploaded file',
                'Click <strong>Save Changes</strong> to persist your settings',
              ]} />
              <Tip>
                When you generate a BREX document using the BREX Builder, the Project slot is automatically updated
                with the generated DMC. You only need to manually set the other slots (S1000D Default, Department,
                Division, Organisation) if they apply to your project.
              </Tip>

              <SubHeading>4.5 Forecast — Projected Completion and Workload</SubHeading>
              <P>
                The <strong>Forecast</strong> card on the Default tab predicts when your DMRL authoring work will
                be complete, based on your team's actual authoring velocity. It compares your recent pace against
                the pace required to hit your next delivery milestone.
              </P>
              <Figure src="17_forecast.jpeg" caption="Figure 5a — The Forecast card showing projected completion, velocity, required pace and weekly output" token={token} />
              <P>The card contains four tiles and a chart:</P>
              <BulletList items={[
                '<strong>Projected Completion</strong> — The forecast date when all DMRL data modules will be authored, with the weeks of work remaining.',
                '<strong>Authoring Velocity</strong> — Data modules authored per week, based on your recent (last 4 weeks) pace.',
                '<strong>Required Pace</strong> — The DMs/week needed to hit your next delivery milestone, shown with the milestone name and date (e.g., "to hit IPD Delivery · 30 Sept 2026").',
                '<strong>Remaining DMs</strong> — Outstanding data modules out of the DMRL total.',
                '<strong>Weekly authoring output</strong> — A bar chart of DMs authored per week over the last 8 weeks, with an amber dashed reference line marking the required pace.',
              ]} />
              <P>
                The status badge in the card header summarises schedule health: <strong>On Track</strong> (projected
                completion is on or before the milestone), <strong>At Risk</strong> (up to 2 weeks late),
                <strong> Off Track</strong> (more than 2 weeks late), <strong>No Milestone Set</strong> (add a
                delivery milestone to enable pace tracking), or <strong>DMRL Complete</strong>.
              </P>
              <Tip>
                The forecast needs a DML (to know the total scope) and at least one authored data module (to measure
                velocity). Add named delivery milestones in the project modal (Section 3.4) to unlock the Required
                Pace and On Track / At Risk / Off Track status.
              </Tip>

              <SubHeading>4.6 Publication Builds</SubHeading>
              <P>
                The <strong>Publication Builds</strong> card tracks the outcome of every PDF publication build run
                for the project — across all three publish pipelines (authored data modules, working directory, and
                custom XSL-FO stylesheets). Failed builds are captured with the exact error message so problems
                never slip by unnoticed.
              </P>
              <Figure src="18_publication_builds.jpeg" caption="Figure 5b — Publication Builds card showing build outcomes and success rate" token={token} />
              <BulletList items={[
                '<strong>Success rate badge</strong> — Overall percentage of successful builds (green ≥90%, amber ≥60%, red below).',
                '<strong>Succeeded counter</strong> — e.g., "2/4 succeeded" across the recorded builds.',
                '<strong>Recent builds list</strong> — Each row shows a green tick (success) or red cross (failure), the publication title, the failure reason for failed builds, the publish pipeline (Authored DMs / Working Dir / XSL-FO), the DM count, the user who ran the build, and the timestamp.',
              ]} />
              <P>
                Builds are recorded automatically whenever anyone runs the PDF Publish tool (see Section 10) — no
                setup is required. The card appears once the first build has been run.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 5. The Tools Screen */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="tools-screen">5. The Tools Screen</SectionHeading>
              <P>
                The Tools screen is your launchpad for all S1000D documentation tools. Click <strong>Tools</strong> in the
                top navigation bar to access it. You can also access tools from within a project via the Tools and Actions tab.
              </P>
              <Figure src="05_tools.jpeg" caption="Figure 8 — The Tools screen showing all available S1000D tools" token={token} />
              <P>Each tool is presented as a card with its icon, name, description, and key capabilities. The seven tools available are:</P>
              <BulletList items={[
                '<strong>Delivery</strong> — Compare DDN and DML files for delivery validation. Identifies matched, DDN-only, and DML-only data modules.',
                '<strong>DML Builder</strong> — Create and manage Data Module Lists. Supports manual entry and automatic scanning of working directories.',
                '<strong>BREX Validation</strong> — Validate data modules against BREX rules and S1000D schema requirements. Results feed into project KPIs.',
                '<strong>BREX Builder</strong> — Build Business Rules Exchange documents with BRDP management, issues tracking, and Excel import.',
                '<strong>Author</strong> — Create S1000D data modules with a split-view structure editor and real-time XML preview.',
                '<strong>PDF Publish</strong> — Generate professional PDF publications from Publication Modules using standard or custom encrypted stylesheets.',
                '<strong>STE Check</strong> — Check data module text against ASD-STE100 Issue 9 Simplified Technical English rules.',
              ]} />
              <P>
                When you access a tool from within a project, the project is automatically selected. The <strong>Back to Project</strong>
                button in the top-left returns you to the project dashboard.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 6. Authoring Data Modules */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="authoring">6. Authoring Data Modules</SectionHeading>
              <P>
                The Author tool allows you to create S1000D-compliant data modules with a split-view editor.
                The left panel provides the structure editor where you build your content, and the right panel
                shows a real-time XML preview of the generated S1000D markup.
              </P>
              <Figure src="06_author.jpeg" caption="Figure 9 — The Author tool with data module listing" token={token} />

              <SubHeading>6.1 Working Directory</SubHeading>
              <P>
                The Author tool supports linking a local working directory using your browser's File System Access API.
                This allows you to browse existing data modules directly from your local file system. Once linked, the
                directory persists across page navigations — you won't need to re-link it each time.
              </P>
              <P>
                To link a directory, click the <strong>Link Working Directory</strong> button and select the folder
                containing your S1000D files. The browser will ask for permission to access this folder.
              </P>
              <Tip>
                Working directory access requires Chrome or Microsoft Edge. Safari and Firefox do not currently
                support the File System Access API. The linked directory is remembered using IndexedDB, so it
                persists even when you navigate between pages.
              </Tip>

              <SubHeading>6.2 Creating a Data Module</SubHeading>
              <P>To create a new data module, follow these steps:</P>
              <NumberedList items={[
                'Select a project from the dropdown at the top of the page',
                'Enter the Data Module Code (DMC) components: Model Ident Code, System Difference Code, System Code, Sub-system codes, Assembly Code, Disassembly Code, Information Code, and Item Location Code',
                'Choose the schema type — <strong>Descriptive</strong> (for reference information) or <strong>Procedural</strong> (for step-by-step instructions)',
                'Add content using the structure editor — click the <strong>+</strong> buttons to add paragraphs, lists, notes, warnings, cautions, and figures',
                'Review the generated XML in the right panel preview to ensure compliance',
                'Click <strong>Save</strong> to store the data module, or <strong>Export XML</strong> to download the file',
              ]} />

              <SubHeading>6.3 S1000D Structure Rules</SubHeading>
              <P>
                The Author tool enforces S1000D structural rules automatically. For example, lists (randomList,
                sequentialList) must be nested inside para elements, not alongside them. When you add a list, the
                tool automatically wraps it in the correct parent elements. Similarly, warnings and cautions follow
                the S1000D nesting hierarchy.
              </P>

              <SubHeading>6.4 Content Types</SubHeading>
              <P>The structure editor supports the following content types:</P>
              <BulletList items={[
                '<strong>Paragraph (para)</strong> — Standard text blocks. The fundamental content unit.',
                '<strong>Random List (randomList)</strong> — Unordered bullet lists. Must be nested within a paragraph.',
                '<strong>Sequential List (sequentialList)</strong> — Numbered step-by-step lists for procedures.',
                '<strong>Note</strong> — Informational callout for supplementary information.',
                '<strong>Warning</strong> — Safety-critical information about potential injury or death.',
                '<strong>Caution</strong> — Information about potential equipment damage.',
                '<strong>Figure</strong> — Image references with title and ICN (Illustration Control Number). Figures also support interactive <strong>hotspots</strong> (see Section 6.7).',
              ]} />

              <SubHeading>6.5 Publication Module (PM) Authoring</SubHeading>
              <P>
                When you open or create a data module with the <strong>Publication Module</strong> schema
                (pm.xsd), the Author tool switches to the dedicated PM Editor. This editor lets you
                build the hierarchical structure of a publication by defining <code>pmEntry</code> elements
                and their child references.
              </P>
              <P>A Publication Module defines the structure and table of contents for a published document. It references other data modules, publication modules, and external publications.</P>
              <SubHeading>6.5.1 PM Structure</SubHeading>
              <P>The PM Editor provides a tree-based interface for building the publication structure. The key building blocks are:</P>
              <BulletList items={[
                '<strong>pmEntry</strong> — A publication entry (like a chapter or section). Each pmEntry has a title and can contain children of any type, including nested pmEntry elements for sub-chapters.',
                '<strong>dmRef</strong> — A reference to a data module. Enter the full DMC components (Model Ident, System Code, etc.) or use the <strong>quick-select dropdown</strong> to pick from your DML entries or working directory files.',
                '<strong>pmRef</strong> — A reference to another publication module. Specify the PM Code components (Model Ident, PM Issuer, PM Number, PM Volume).',
                '<strong>externalPubRef</strong> — A reference to an external publication outside the S1000D data set. Enter the external publication code and title.',
              ]} />
              <SubHeading>6.5.2 Building a Publication Module</SubHeading>
              <P>To create a Publication Module:</P>
              <NumberedList items={[
                'Select a project and choose <strong>Publication Module</strong> as the schema type when creating a new data module, or open an existing PMC-*.xml file from your working directory',
                'Click <strong>Add pmEntry</strong> to create a top-level publication entry (e.g., "Chapter 1 — Introduction")',
                'Give the pmEntry a title in the text field',
                'Inside each pmEntry, use the <strong>+ dmRef</strong>, <strong>+ pmRef</strong>, <strong>+ pmEntry</strong>, or <strong>+ externalPubRef</strong> buttons to add child references',
                'For dmRef entries, use the <strong>Select from DML / directory</strong> dropdown to quickly populate all DMC fields from your existing DML entries or working directory XML files',
                'Nest pmEntry elements inside other pmEntry elements to create multi-level publication structures (e.g., chapters with sub-sections)',
                'Review the generated XML preview on the right panel to verify the S1000D-compliant <code>&lt;pm&gt;</code> markup',
                'Click <strong>Save</strong> or <strong>Export XML</strong> when complete',
              ]} />
              <Tip>
                The PM Editor supports recursive nesting — you can create deeply nested publication structures
                with chapters, sub-chapters, and sub-sub-chapters. The tree view uses indentation to show
                the hierarchy clearly. Collapse or expand any pmEntry by clicking the chevron arrow.
              </Tip>

              <SubHeading>6.6 Reordering Steps and Content</SubHeading>
              <P>
                The Author tool lets you rearrange your content without cutting and pasting. Reordering works at
                two levels:
              </P>
              <BulletList items={[
                '<strong>Step / section reorder</strong> — Every proceduralStep, levelledPara, and crewDrillStep header has a drag handle (grip icon) plus up/down arrow buttons. Drag a step onto another to swap positions, or click the arrows to nudge it one place. Spec-compliant IDs are renumbered automatically after a move.',
                '<strong>Element reorder</strong> — Inside any step or levelledPara, hover over a paragraph, figure, table, list, note, warning, or caution to reveal a grip handle with up/down arrows on its left edge. Drag the element onto another row, or use the arrows, to change the content order.',
              ]} />
              <Figure src="21_element_reorder.jpeg" caption="Figure 9a — Hover an element to reveal the reorder grip and up/down arrows on the left edge" token={token} />
              <P>To reorder elements inside a step:</P>
              <NumberedList items={[
                'Open the data module and expand the step or levelledPara containing the content',
                'Hover your mouse over the element you want to move — the grip handle and arrows appear on the left',
                'Either click the <strong>up</strong>/<strong>down</strong> arrows to move it one position, or hold the grip handle and <strong>drag</strong> the element onto the row where you want it',
                'Check the XML preview on the right — the element order updates immediately',
              ]} />
              <Tip>
                The arrows are disabled at the top and bottom of a block, and the handles only appear when the
                block contains more than one element.
              </Tip>

              <SubHeading>6.7 Figures with Hotspots</SubHeading>
              <P>
                Hotspots make graphics interactive: each hotspot marks a callout region on the figure (per the
                S1000D <code>&lt;hotspot&gt;</code> element inside <code>&lt;graphic&gt;</code>) and can link to
                other content. Every figure element in the Author tool has a <strong>Hotspots</strong> section.
              </P>
              <Figure src="19_hotspots.jpeg" caption="Figure 9b — The Hotspots section of a figure, showing spec-compliant ids, CGM application structure idents, and titles" token={token} />

              <SubHeading>6.7.1 Reading Hotspots from a CGM Graphic</SubHeading>
              <P>
                If your figure's ICN refers to a CGM graphic in your linked working directory, the tool can read the
                hotspot definitions embedded in the CGM (WebCGM application structures) automatically:
              </P>
              <NumberedList items={[
                'Link your working directory (Section 6.1) — it must contain the CGM file whose name matches the figure\'s ICN (e.g., ICN-C0419-S1000D0360-001-01.CGM)',
                'Add a Figure element and set its ICN using the ICN dropdown or by typing it',
                'Click <strong>Read hotspots from CGM</strong> — the tool parses the binary CGM, extracts every callout region, and creates one hotspot per callout',
                'Each hotspot receives a spec-compliant id (e.g., fig-0001-hot-0001), the CGM application structure ident (e.g., hot001), the callout number, and hotspotType CALLOUT',
                'Type a title for each hotspot (e.g., "BRAKE SUB-SYSTEM") — the title is written to both hotspotTitle and objectDescr in the XML',
              ]} />
              <Tip>
                Duplicate callouts in the CGM (e.g., hot002a, hot002b pointing at the same item number) are
                de-duplicated automatically — you get one hotspot per unique callout number. Re-reading the CGM
                keeps any titles and links you have already entered.
              </Tip>

              <SubHeading>6.7.2 Bulk Titles from a Parts List</SubHeading>
              <P>
                Instead of typing every hotspot title, paste a numbered parts list and let the tool match titles to
                callout numbers:
              </P>
              <NumberedList items={[
                'Click <strong>Bulk Titles</strong> in the Hotspots section',
                'Paste your list into the box — one line per hotspot, starting with the callout number (e.g., "1 BRAKE SUB-SYSTEM")',
                'Click <strong>Apply Titles</strong> — each line is matched to the hotspot with the same callout number and the titles are filled in automatically',
              ]} />

              <SubHeading>6.7.3 Hotspot Links (internalRef / dmRef)</SubHeading>
              <P>
                Each hotspot can carry its own link so that clicking the callout in an IETP viewer jumps to the
                right target:
              </P>
              <NumberedList items={[
                'Click the <strong>link</strong> icon on the hotspot row to open the link form',
                'Choose <strong>internalRef</strong> to jump to a target inside this data module — pick from the dropdown of figures, tables, steps and paras, or type a target id and select the target type (irtt01–irtt11)',
                'Choose <strong>dmRef</strong> to jump to another data module — pick a DM from your DML/working directory, or type the DMC code',
                'A violet "→ target" badge appears on the row, and the XML preview shows the <code>&lt;internalRef&gt;</code> or <code>&lt;dmRef&gt;</code> child inside the hotspot',
                'Select <strong>No link</strong> to remove the link again',
              ]} />

              <SubHeading>6.7.4 Previewing Hotspot Regions</SubHeading>
              <P>
                Click <strong>Preview Regions</strong> to see where each callout sits on the graphic. The preview
                draws every hotspot region (read from the CGM) on a schematic canvas — hover a region to identify
                it by ident and title. If a raster copy of the graphic (PNG/JPG with the same ICN name) exists in
                your working directory, it is shown underneath the regions.
              </P>
              <Figure src="20_hotspot_preview.jpeg" caption="Figure 9c — Preview Regions showing the CGM callout locations; hover a numbered region to identify it" token={token} />

              <SubHeading>6.8 Schema Check and XML Preview Aids</SubHeading>
              <P>The Author tool includes several aids for producing valid S1000D XML:</P>
              <BulletList items={[
                '<strong>Schema Check</strong> — Validates the generated XML against the official S1000D XSD schema (descript, proced, crew, pm). Violations are listed with their line numbers.',
                '<strong>Error Line Jump</strong> — Click any schema violation and the XML preview scrolls straight to the offending line.',
                '<strong>Line Numbering Toggle</strong> — Turn line numbers in the XML preview on or off to suit your reading preference.',
                '<strong>Automatic spec-compliant IDs</strong> — All id attributes (fig-0001, tab-0001, stp-0001, cap-0001, hotspots, crew drill steps, etc.) are generated and renumbered automatically in the S1000D-required format — you never need to invent ids by hand.',
              ]} />

              <SubHeading>6.9 Notes, Warnings and Cautions</SubHeading>
              <P>
                Notes, warnings and cautions are full multi-paragraph elements. Press <strong>Enter</strong> inside
                the text box to start a new paragraph — each line becomes its own <code>&lt;notePara&gt;</code> (notes)
                or <code>&lt;warningAndCautionPara&gt;</code> (warnings/cautions), and a live counter shows how many
                paragraphs will be generated.
              </P>
              <Figure src="26_warning_attention.jpeg" caption="Figure 9a — A multi-paragraph warning with an attentionRandomList" token={token} />
              <BulletList items={[
                '<strong>Ordering rules</strong> — In a levelledPara or proceduralStep a Warning can only be added while it would be the first element; a Caution can only follow warnings. Once a paragraph or other content exists, both buttons are disabled with an explanatory tooltip.',
                '<strong>Attention Random List (warning/caution)</strong> — Click <em>+ Attention Random List</em> inside a warning or caution to add an <code>attentionRandomList</code>. Only one is allowed per warning/caution, and nested lists are not permitted by the schema.',
                '<strong>Attention lists in notes</strong> — Notes support both <em>+ Attention Random List</em> and <em>+ Attention Sequential List</em>. Each sequential list item has a <em>+ Random</em> button to embed one <code>attentionRandomList</code> — once any item has one, the button is disabled on the others.',
              ]} />

              <SubHeading>6.10 Tables</SubHeading>
              <P>
                CALS tables in the Author tool are fully interactive — what you see in the grid matches the
                generated <code>colspec</code>, merge and alignment markup.
              </P>
              <Figure src="25_table_editor.jpeg" caption="Figure 10a — The CALS table editor with per-column controls, merged cells and an in-cell warning" token={token} />
              <BulletList items={[
                '<strong>Column widths & resize</strong> — The colwidth values (e.g. 40*) are reflected in the grid. Drag the small handles between columns to resize, or type a value in the Columns row.',
                '<strong>Insert/delete specific columns</strong> — Use the small + buttons between the column chips to insert a column at any position, and the trash icon on a chip to delete that exact column. Merged cells are adjusted automatically.',
                '<strong>Cell options menu</strong> — Hover a cell and click the … button: set horizontal alignment (left/center/right) and vertical alignment (top/middle/bottom). Alignment set on a <em>header</em> cell propagates to every body cell in that column; a body cell can still override it.',
                '<strong>Merge cells</strong> — From the cell menu choose <em>Merge Right</em> (horizontal, emits namest/nameend) or <em>Merge Down</em> (vertical, emits morerows). Merged cells show a violet ring; <em>Unmerge</em> restores them.',
                '<strong>Row reorder</strong> — Hover a body row to reveal a grip handle with up/down arrows at its right end. Drag the grip onto another row, or click the arrows, to change the row order. Vertically merged rows always move together as a single block, so merges are never broken.',
                '<strong>Warning / Caution / Note in cells</strong> — The cell menu\'s Add row inserts these blocks with the same ordering rules: a warning only in an empty cell, a caution only before para/note content.',
                '<strong>Growing cells & multi-para</strong> — Cells grow as you type; press Enter for a new line and each line becomes its own <code>&lt;para&gt;</code> in the entry.',
                '<strong>Header row</strong> — Delete the header row with the trash icon at its right end (no <code>&lt;thead&gt;</code> is emitted); restore it with <em>+ Add Header Row</em>.',
              ]} />

              <SubHeading>6.11 Nested levelledParas, Multi-Graphic Figures and Definition Lists</SubHeading>
              <BulletList items={[
                '<strong>Nested levelledPara</strong> — Click <em>+ levelledPara</em> in a descriptive levelledPara to nest sub-sections, up to 4 levels deep. At the fourth level the button is replaced by a "max 4 levelledPara levels reached" hint.',
                '<strong>Multiple graphics per figure</strong> — Click <em>+ Graphic</em> on a figure to add further ICN references; each becomes an additional <code>&lt;graphic&gt;</code> element (and DOCTYPE entity).',
                '<strong>Definition lists</strong> — Click <em>+ Definition List</em> on a figure (emitted as <code>&lt;legend&gt;&lt;definitionList&gt;</code> below the graphics) or <em>+ definitionList</em> on a paragraph. Each item has a text-only <code>listItemTerm</code> and a <code>listItemDefinition</code> where Enter creates additional <code>&lt;para&gt;</code> elements. When previewing hotspot regions, the figure\'s definition list is rendered as a legend beside the graphic.',
                '<strong>Hotspot application structure fields</strong> — Every hotspot row has editable <em>APS ident</em> and <em>APS name</em> inputs which populate the <code>applicationStructureIdent</code> and <code>applicationStructureName</code> attributes.',
              ]} />

              <SubHeading>6.12 Automatic refs Block</SubHeading>
              <P>
                Whenever you insert a <code>dmRef</code>, <code>pmRef</code> or <code>externalPubRef</code> in a
                paragraph, the Author tool automatically maintains a <code>&lt;refs&gt;</code> block as the first
                child of <code>&lt;content&gt;</code>, listing each unique reference. Deleting the inline reference
                removes it from the block; when the last one goes, the whole <code>&lt;refs&gt;</code> block disappears.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 7. BREX Builder */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="brex-builder">7. BREX Builder</SectionHeading>
              <P>
                The BREX Builder allows you to create and manage Business Rules Exchange (BREX) data modules.
                BREX documents define the specific business rules that apply to your project's data modules,
                including which elements and attributes are allowed, required, or forbidden.
              </P>
              <Figure src="08_brex_builder.jpeg" caption="Figure 10 — The BREX Builder with BRDP decisions grid" token={token} />

              <SubHeading>7.1 BRDP Management</SubHeading>
              <P>The BREX Builder uses a tabbed interface with three main areas:</P>
              <BulletList items={[
                '<strong>Definitions</strong> — Manage BREX Rule Decision Points (BRDPs). Each BRDP contains an XPath expression, severity level (error, warning, info), and a description. You can add, edit, and delete rules in a spreadsheet-like data grid.',
                '<strong>Issues</strong> — Track issues and decisions related to business rules. Record the rationale behind rule decisions for audit trails.',
                '<strong>Generated Documents</strong> — View and download previously generated BREX XML and PDF files. Each generation is timestamped and versioned.',
              ]} />

              <SubHeading>7.2 Importing from Excel</SubHeading>
              <P>
                You can bulk-import BREX definitions from an Excel spreadsheet. Click <strong>Import from Excel</strong>
                and upload an .xlsx file with columns for Rule ID, XPath, Severity, and Description. The tool will
                parse the spreadsheet and add all entries to your BREX definition list. Existing rules with matching
                Rule IDs will be updated.
              </P>
              <Tip>
                Download the BREX template from the Resources page to ensure your Excel file has the correct column
                format before importing.
              </Tip>

              <SubHeading>7.3 Generating BREX Output</SubHeading>
              <P>
                Once your definitions are complete, click <strong>Generate BREX XML</strong> to create a fully compliant
                S1000D BREX data module. The generated brDoc XML file follows the S1000D schema and can be used
                directly in your validation workflow. You can also generate a PDF summary of all rules for review
                and sign-off purposes.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 8. BREX Validation */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="brex-validation">8. BREX Validation</SectionHeading>
              <P>
                The BREX Validation tool checks your data modules against Business Rules Exchange specifications
                and S1000D schema requirements. It evaluates each data module against the XPath rules defined in
                your BREX document and reports any violations.
              </P>
              <Figure src="09_brex_validation.jpeg" caption="Figure 11 — BREX Validation with source selection and history" token={token} />

              <SubHeading>7.1 Running a Validation</SubHeading>
              <P>To validate your data modules against BREX rules:</P>
              <NumberedList items={[
                'Select a project from the dropdown at the top',
                'In the <strong>Source Selection</strong> area, select the folder containing your BREX modules — these are XML files with information code 022 (brDoc)',
                'Select the folder containing the data modules to be checked',
                'Click the <strong>BREX Validation</strong> button to run the check',
              ]} />
              <P>
                The tool will evaluate each data module against every BRDP rule in the selected BREX file and produce
                a detailed results report.
              </P>

              <SubHeading>8.2 Validation Results</SubHeading>
              <P>
                Results are displayed showing pass/fail status for each data module, with expandable details of any
                BREX rule violations. Each violation shows the rule ID, XPath expression, severity, and the specific
                location in the data module where the violation was found.
              </P>
              <P>
                Results are automatically saved to the project history and contribute to the BREX Compliance KPI on
                the project dashboard. Over time, this builds a compliance trend that helps track your project's
                quality improvement.
              </P>

              <SubHeading>8.3 Validation History</SubHeading>
              <P>
                The History panel on the right shows all previous validation runs with their results, dates, and
                pass/fail counts. You can review previous results at any time or delete old entries that are no
                longer relevant.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 9. DML Builder */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="dml-builder">9. DML Builder</SectionHeading>
              <P>
                The DML Builder tool helps you create and manage Data Module Lists (DMLs). A DML is a manifest
                of all data modules that form part of a delivery or project scope. It is essential for tracking
                what needs to be produced, delivered, and validated.
              </P>
              <Figure src="07_dml_builder.jpeg" caption="Figure 12 — The DML Builder showing DML Information form" token={token} />

              <SubHeading>9.1 Building a DML</SubHeading>
              <P>There are two ways to build your DML:</P>
              <BulletList items={[
                '<strong>Manual entry</strong> — Add entries one by one by specifying Model Ident Code, System Difference Code, System Code, Sub-system codes, Assembly Code, Disassembly Code, Information Code, and Item Location Code',
                '<strong>Auto-scan</strong> — Use the <strong>View List</strong> feature to scan your working directory for existing DMC-*.xml files and automatically populate your DML entries',
              ]} />
              <P>
                Once your DML is complete, click <strong>Export DML XML</strong> to generate an S1000D-compliant DML
                file that can be used in delivery comparisons and project tracking.
              </P>

              <SubHeading>9.2 Working Directory Integration</SubHeading>
              <P>
                Like the Author tool, the DML Builder supports linking a working directory via the browser's File
                System Access API. Click <strong>View List</strong> to scan the linked directory for DMC-*.xml files
                and automatically populate your DML entries. This is the fastest way to create a DML from an existing
                set of data modules.
              </P>
              <Tip>
                Ensure your data module filenames follow the standard S1000D naming convention (DMC-*.xml) for
                automatic detection. Files that don't match this pattern will not be picked up by the scanner.
              </Tip>

              <SubHeading>9.3 Data Dispatch Note (DDN) Builder</SubHeading>
              <P>
                The DML Builder includes a dedicated <strong>DDN</strong> tab for creating S1000D-compliant Data Dispatch Notes.
                A DDN is a formal manifest that accompanies a data delivery — it lists exactly which data modules,
                publication modules, and illustration files are being dispatched from a sender to a receiver.
              </P>
              <P>
                The DDN tab is accessed from the pill-shaped tab bar at the top of the DML Builder. Click <strong>DDN</strong> to switch to the DDN form.
              </P>
              <Figure src="16_ddn_builder.jpeg" caption="Figure 13 — DDN Builder showing metadata form and dispatch address fields" token={token} />
              <SubHeading>9.3.1 DDN Metadata</SubHeading>
              <P>Fill in the DDN form fields to define the dispatch note metadata:</P>
              <BulletList items={[
                '<strong>Model Identification Code</strong> — Select from the project\'s configured model ident codes, or type one manually.',
                '<strong>Sequence Number</strong> — A 5-digit sequential dispatch note number (e.g., 00001). Incremented for each new dispatch.',
                '<strong>Sender Ident</strong> — The CAGE code or identifier of the sending organisation. If your project has Responsible Partner Companies configured, you can select from a dropdown.',
                '<strong>Receiver Ident</strong> — The CAGE code or identifier of the receiving organisation.',
                '<strong>Year of Data Issue</strong> — The year the data is being dispatched.',
                '<strong>Security Classification</strong> — The security classification level for the DDN.',
                '<strong>DDN Remarks / Title</strong> — A descriptive title or remark for the dispatch (e.g., "Initial Data Delivery").',
              ]} />
              <P>
                A live <strong>DDN Code Preview</strong> at the top of the form shows how the DDN filename will be
                constructed from your inputs (e.g., DDN-DEMO-CAGE1-CAGE2-2026-00001).
              </P>
              <SubHeading>9.3.2 Dispatch Addresses</SubHeading>
              <P>
                Below the metadata fields, you can enter full postal addresses for both the sender and receiver organisations:
              </P>
              <BulletList items={[
                '<strong>Dispatch To (Receiver)</strong> — Enterprise Name, Street, City, Postal Code, Country of the receiving organisation.',
                '<strong>Dispatch From (Sender)</strong> — Enterprise Name, Street, City, Postal Code, Country of the sending organisation. The Enterprise Name auto-populates when you select a Sender from the dropdown.',
              ]} />
              <SubHeading>9.3.3 Saving and Building the DDN</SubHeading>
              <P>The DDN workflow has two steps:</P>
              <NumberedList items={[
                '<strong>Save DDN Information</strong> — Click the <strong>Save DDN Information</strong> button to persist the DDN metadata to the project. This must be done before building. You can update the saved DDN at any time.',
                '<strong>Build DDN</strong> — After saving, click <strong>Build DDN</strong> to open the Build dialog. This shows all Data Modules, Publication Modules, and ICN files available from your DML entries and working directory.',
              ]} />
              <SubHeading>9.3.4 Build DDN Dialog</SubHeading>
              <P>
                The Build DDN dialog presents a checklist of all files that can be included in the delivery list:
              </P>
              <BulletList items={[
                '<strong>DM entries</strong> — Data Modules from your DML list, shown with a blue badge.',
                '<strong>PM entries</strong> — Publication Modules from your DML list, shown with a violet badge.',
                '<strong>ICN files</strong> — Illustration files (ICN-*) automatically detected from your linked working directory, shown with an amber badge. These are listed in a separate section at the bottom.',
                '<strong>Select All / Deselect All</strong> — Quickly toggle all files on or off.',
                '<strong>Include/Remove All ICNs</strong> — Toggle all illustration files as a group.',
              ]} />
              <P>
                Once you have selected the files to include, click <strong>Generate DDN XML</strong> to build a
                fully S1000D-compliant DDN XML document. The generated XML includes the complete
                <code> identAndStatusSection</code> (with ddnCode, dispatch addresses, security, and BREX references)
                and the <code>ddnContent</code> with all selected delivery list items.
              </P>
              <Tip>
                When you click Generate, the selected DM files are scanned for ICN references and any matching
                illustration files from the working directory are automatically added to the delivery list — so the
                DDN always lists the graphics belonging to the data modules it dispatches.
              </Tip>
              <SubHeading>9.3.5 DDN XML Export</SubHeading>
              <P>
                After generation, the DDN Export dialog shows the full XML output. You can:
              </P>
              <BulletList items={[
                '<strong>Download XML</strong> — Save the file locally with the standard DDN filename.',
                '<strong>Download DDN Package (ZIP)</strong> — Build a complete delivery package: the DDN XML plus every selected file (DM/PM XML and ICN graphics) read from your linked working directory, downloaded as a single ZIP. Files that cannot be found in the directory are listed in a warning.',
                '<strong>Copy to Clipboard</strong> — Copy the full XML text for pasting into another tool or editor.',
              ]} />
              <Tip>
                The DDN automatically includes BREX references from your project's BREX Modules configuration.
                Make sure you have set up the BREX Modules on the Project Dashboard (BREX tab) before building
                a DDN to ensure the correct <code>&lt;brexDmRef&gt;</code> elements appear in the generated XML.
              </Tip>

              {/* ════════════════════════════════════════ */}
              {/* 10. Publishing to PDF */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="pdf-publish">10. Publishing to PDF</SectionHeading>
              <P>
                The PDF Publish tool generates professional PDF documents from your S1000D Publication Modules (PMCs).
                It reads PMC files from your working directory, follows the publication structure, and assembles
                all referenced data modules, images, and front matter into a single PDF document.
              </P>
              <Figure src="10_pdf_publish.jpeg" caption="Figure 14 — The PDF Publish tool with custom stylesheet options" token={token} />

              <SubHeading>10.1 Standard Publishing</SubHeading>
              <P>To generate a standard PDF publication:</P>
              <NumberedList items={[
                'Link your working directory containing PMC-*.xml files (Publication Module Codes)',
                'The left panel will show all PMC files found in the directory. Select the Publication Module you wish to publish.',
                'Review the publication structure tree — this shows the hierarchy of data modules included in the publication',
                'Click <strong>Print with IDStatus</strong> to include identification and status information, or <strong>Print without IDStatus</strong> for clean output',
              ]} />
              <P>
                The PDF will be generated and downloaded to your browser. Standard publishing uses the built-in
                S1000D stylesheet format.
              </P>

              <SubHeading>10.2 Custom Stylesheet Publishing</SubHeading>
              <P>
                For branded or customer-specific output, you can use a custom XSL-FO stylesheet provided by your
                administrator. Custom stylesheets are encrypted for security:
              </P>
              <BulletList items={[
                'Click <strong>Upload Encrypted Stylesheet (.enc)</strong> to load your encrypted XSL file, or select a previously uploaded server-side stylesheet from the dropdown',
                'Optionally upload custom images to replace defaults: <strong>Logo</strong> (company logo on cover/header), <strong>Warning</strong> symbol, <strong>Caution</strong> symbol, <strong>Safety Notice</strong> border, and <strong>Hazmat Border</strong>',
                'Click <strong>Generate with Custom Stylesheet</strong> to produce the PDF using the Apache FOP XSL-FO transformation engine',
              ]} />
              <Tip>
                Custom stylesheets are encrypted by your administrator using AES encryption. Only .enc (encrypted)
                files are accepted for customer users. This protects the stylesheet intellectual property. Contact
                your administrator if you need a custom stylesheet for your project.
              </Tip>

              <SubHeading>10.3 Build Tracking</SubHeading>
              <P>
                Every PDF build — from standard publishing, working-directory publishing, or custom stylesheet
                publishing — is recorded automatically with its outcome. Successful builds are logged with the
                publication title, DM count, user and time; failed builds also capture the exact error message.
                Review all build outcomes on the <strong>Publication Builds</strong> card of the Project Dashboard
                (see Section 4.6).
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 11. Delivery */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="delivery">11. Delivery</SectionHeading>
              <P>
                The Delivery tool compares Data Dispatch Note (DDN) files against Data Module List (DML) files
                to ensure consistency between what was dispatched and what was expected. This is essential for
                verifying that all required data modules have been delivered correctly.
              </P>
              <Figure src="12_delivery.jpeg" caption="Figure 15 — The Delivery comparison tool" token={token} />

              <SubHeading>11.1 Running a Comparison</SubHeading>
              <P>To run a delivery comparison:</P>
              <NumberedList items={[
                'Select your project from the dropdown',
                'Upload or select your DDN file(s) — these are the dispatch notes listing what was actually sent',
                'Upload or select your DML file(s) — these are the master lists of what was expected',
                'Click <strong>Compare</strong> to run the comparison',
              ]} />

              <SubHeading>11.2 Understanding Results</SubHeading>
              <P>The comparison results are colour-coded for quick identification:</P>
              <BulletList items={[
                '<strong>Matched</strong> (green) — Data modules found in both DDN and DML. These are correctly delivered.',
                '<strong>DDN Only</strong> (amber) — Data modules in the dispatch note but not in the requirements list. These may be extras or errors.',
                '<strong>DML Only</strong> (red) — Data modules required by the DML but not found in the dispatch note. These are missing deliverables.',
              ]} />
              <P>
                The Delivery Match percentage is calculated and tracked on the project dashboard KPIs. Over time,
                this helps monitor delivery completeness across multiple dispatch cycles.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 12. STE Check */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="ste-check">12. STE Check</SectionHeading>
              <P>
                The STE Check tool validates your data modules against ASD-STE100 Issue 9 (Simplified Technical
                English) rules. It reads DM XML files from your working directory, extracts all text content, and
                checks it against seven compliance rules designed to ensure clear, unambiguous technical writing.
              </P>
              <Figure src="11_ste_check.jpeg" caption="Figure 16 — The STE Check tool" token={token} />

              <SubHeading>12.1 Running an STE Check</SubHeading>
              <P>To check your data modules for STE compliance:</P>
              <NumberedList items={[
                'Select your project from the dropdown — this loads the project\'s Technical Nouns list',
                'Link your working directory containing DMC-*.xml files',
                'The left panel shows all DM files found in the directory with a file count',
                'Click <strong>Check STE Compliance</strong> to run the check against all DMs simultaneously',
              ]} />
              <P>
                The check may take a few moments depending on the number and size of your data modules. Results
                appear in the right panel.
              </P>

              <SubHeading>12.2 STE Issue 9 Rules</SubHeading>
              <P>The STE Check validates against seven rules based on ASD-STE100 Issue 9:</P>
              <BulletList items={[
                '<strong>STE-1 — Approved Vocabulary:</strong> Only use words from the STE-approved dictionary. Non-approved words are flagged with suggested replacements where available. Common technical terms, abbreviations, and numbers are automatically excluded.',
                '<strong>STE-2 — Technical Nouns:</strong> Project-specific technical terms must be defined in the project\'s Technical Nouns list (managed in Project Settings). Any technical word not on the list is flagged.',
                '<strong>STE-3 — Sentence Length:</strong> Maximum 20 words for procedural text (instructions), 25 words for descriptive text (explanations). Longer sentences are flagged with the actual word count.',
                '<strong>STE-4 — One Topic Per Sentence:</strong> Each sentence should contain only one instruction or topic. Sentences with multiple clauses joined by "and", "but", "or" are flagged.',
                '<strong>STE-5 — Paragraph Length:</strong> Maximum 6 sentences per paragraph. Longer paragraphs should be broken down for readability.',
                '<strong>STE-6 — Active Voice:</strong> Use active voice. Passive constructions (e.g., "was installed", "is required") are flagged with suggestions to rewrite in active voice.',
                '<strong>STE-7 — No \'-ing\' Starts:</strong> Procedural steps should not begin with gerund (-ing) forms (e.g., "Installing the part" should be "Install the part").',
              ]} />

              <SubHeading>12.3 Understanding Results</SubHeading>
              <P>
                Results are displayed per data module, with an overall compliance percentage at the top. Each DM row
                shows the filename, compliance score, and a summary of rules passed/failed. Click on any DM row to
                expand and see:
              </P>
              <BulletList items={[
                'Individual rule results (pass/fail) with violation counts',
                'Specific violation details including the offending text, location, and suggested corrections',
                'A per-rule breakdown showing exactly which sentences or words triggered each rule',
              ]} />
              <P>
                Results are automatically saved and displayed on the Project Dashboard's STE Compliance summary card.
                This allows project managers to monitor STE compliance trends over time.
              </P>
              <Tip>
                Technical Nouns are managed in Project Settings, not in the STE Check tool. Add your project-specific
                terms there <strong>before</strong> running a check to avoid false positives on valid technical vocabulary.
                You can import nouns from an Excel spreadsheet for bulk entry.
              </Tip>

              {/* ════════════════════════════════════════ */}
              {/* 13. Your Account and Security */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="account-security">13. Your Account and Security</SectionHeading>
              <Figure src="13_settings.jpeg" caption="Figure 17 — Profile Settings" token={token} />

              <SubHeading>13.1 Profile Settings</SubHeading>
              <P>
                Click your name in the top-right corner and select <strong>Settings</strong> to view your profile.
                You can update your first name and last name. Your email address is set by your administrator and
                cannot be changed. Your company name and role are also displayed for reference.
              </P>

              <SubHeading>13.2 Changing Your Password</SubHeading>
              <P>
                There are two ways to change your password:
              </P>
              <BulletList items={[
                '<strong>From the user menu</strong> — Click your name in the header, then select <strong>Change Password</strong>. A modal will appear where you enter your current password and your new password.',
                '<strong>From Security settings</strong> — Navigate to Security &amp; MFA from the user menu. The password change form is available in the Security section.',
              ]} />
              <P>
                Passwords must be at least 8 characters. Use a mix of uppercase, lowercase, numbers, and symbols
                for best security.
              </P>

              <SubHeading>13.3 Multi-Factor Authentication</SubHeading>
              <P>
                Your administrator may enable Multi-Factor Authentication (MFA) for your account. When MFA is
                enabled, you will be prompted for a time-based one-time password (TOTP) after entering your
                email and password. To set up MFA:
              </P>
              <NumberedList items={[
                'Navigate to <strong>Security &amp; MFA</strong> from the user menu',
                'If MFA is available, click <strong>Enable MFA</strong>',
                'Scan the QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, or Authy)',
                'Enter the 6-digit code from your authenticator app to confirm setup',
              ]} />

              <SubHeading>13.4 Company and Licence</SubHeading>
              <P>
                Each company has a licence that determines the maximum number of users (up to 1,000, set by your
                administrator). Your Account Information section shows when your account was created, your current
                MFA status, and your company details. If you need additional user licences, contact your
                MySpecWorkspace administrator.
              </P>

              {/* ════════════════════════════════════════ */}
              {/* 14. History & Audit Trail */}
              {/* ════════════════════════════════════════ */}
              <SectionHeading id="history-audit">14. History &amp; Audit Trail</SectionHeading>
              <P>
                The <strong>History</strong> menu gives your company's <strong>Primary user</strong> a complete,
                company-isolated audit trail of activity in MySpecWorkspace. Only the Primary user sees the History
                menu item — team members and administrators cannot view your company's history, and you can never
                see another company's records.
              </P>

              <SubHeading>14.1 Logons Tab</SubHeading>
              <P>
                Lists every sign-in by your company's users — user name, email address and the date/time of each
                logon, newest first.
              </P>

              <SubHeading>14.2 Actions Tab</SubHeading>
              <P>The Actions tab records who did what, and when. The following activities are logged automatically:</P>
              <BulletList items={[
                '<strong>Projects</strong> — created, updated, deleted or imported',
                '<strong>Estimator projects</strong> — estimates created, updated or deleted',
                '<strong>DMLs and DMC entries</strong> — DML created/updated/deleted, entries added or removed',
                '<strong>Conversion runs</strong> — each batch conversion with the number of documents',
                '<strong>BREX validations</strong> — each run with the DM count and pass rate',
                '<strong>PDF publishes</strong> — successful and failed publication builds',
                '<strong>Author saves</strong> — every data module save or delete (with DMC and title)',
                '<strong>STE checks</strong> — text and data-module checks',
                '<strong>DDN builds</strong> — each generated Data Dispatch Note',
              ]} />
              <P>
                Each entry shows the team member, a colour-coded action badge, the item affected, the related
                project and the date/time.
              </P>

              <SubHeading>14.3 Filters</SubHeading>
              <P>
                Use the filter bar above the tabs to narrow both lists: pick a <strong>team member</strong>, an{' '}
                <strong>action type</strong> (Actions tab), and/or a <strong>From/To date range</strong>. The tab
                counters show how many records match (e.g. "Actions (12 of 480)"). Click{' '}
                <strong>Clear filters</strong> to reset.
              </P>

              <SubHeading>14.4 CSV Export</SubHeading>
              <P>
                Click <strong>Export Logons CSV</strong> or <strong>Export Actions CSV</strong> to download the full
                history as a spreadsheet-ready CSV file (named after your company) — useful for compliance records
                and offline review.
              </P>

              <div className="mt-10 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 italic">
                  For further assistance, contact your MySpecWorkspace administrator or visit the Resources section of the application.
                  You can also reach TIM Consulting via the "Contact TIM Consulting" link in the user menu.
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
